
interface FHIRMetadata {
  rest: Array<{
    security: {
      extension: Array<{
        url: string;
        extension?: Array<{
          url: string;
          valueUri?: string;
        }>;
      }>;
    };
  }>;
  software?: {
    name: string;
    version?: string;
  };
  publisher?: string;
}

interface HealthSystemEndpoint {
  resourceType: string;
  status: string;
  contained?: Array<{
    resourceType: string;
    name: string;
    address?: Array<{
      line: string[];
      city: string;
      state: string;
      postalCode: string;
      country: string;
    }>;
  }>;
  name: string;
  id: string;
  address: string;
}

// Helper function to get client ID for specific servers - NOW EXPORTED
export const getClientIdForServer = (metadata: FHIRMetadata, serverId: string): string | null => {
  console.log('Getting client ID for server:', serverId, metadata);
  
  // Check for athenahealth
  if (metadata.software && metadata.software.name.indexOf("athenahealth") !== -1) {
    return '0oak11xvyobnlP0qL297';
  }

  // Check for Epic
  if (metadata.software && metadata.software.name === "Epic") {
    if (serverId === '30ce9aea-96dd-416a-935d-d07b157bd2cf') {
      // sandbox
      return '35772ca5-efb2-416c-ad6f-641010bfa62a';
    }

    if (metadata.software.version === "August 2024" || 
        metadata.software.version === "November 2024" || 
        (metadata.software.version && metadata.software.version.indexOf('2025') >= 0)) {
      return '156abfaa-1661-4d0d-a8bc-aa6c5369ed77';
    }

    // Production
    return 'e728a9c6-7b09-4c05-97ce-fb0f1aff5838';
  }

  // Check for eClinicalWorks
  if (metadata.software && metadata.software.name === "eClinicalWorks") {
    // Production with new app
    return '0c406c78abae40678cb59da86bede03d';
  }

  // Check for eCW FHIR Facade
  if (metadata.software && metadata.software.name === "eCW FHIR Facade") {
    return 'HVKi3Jrt9lUZGpWeayT-bgQUr2IoXHMNWGZdyVyxdUI';
  }

  // Check for Cerner
  if (metadata.publisher && metadata.publisher === "Cerner") {
    // Production
    return 'cd4b6c46-5d37-4f55-9229-441e5df744b3';
  }

  return null;
};

// Helper function to generate state for OAuth
const generateOAuthState = (healthSystemId: string): string => {
  return healthSystemId + "/" + Math.random().toString().substring(3);
};

// Helper function to set cookie
const setCookie = (name: string, value: string, days: number): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

// Helper function to get OAuth scopes based on client ID
const getOAuthScopes = (clientId: string): string => {
  const specialClientIds = [
    "yP7GArxnmCK0FmIftjwD3kc94fxpeZ5fCDNAxm-7r0E",
    "oO5OtwN1ZKqeg_VIEDoiNXbkkOIBRH0WrlCXWjPpJ-0",
    "JcjgosvOOHtRdThNyEsKndvuvLlQXfyKw_4XYwYns2Y",
    "T1SRnrUeCc48fkBjOvFo9d0oOEQGitl9wzn1AD9p0CU",
    "HVKi3Jrt9lUZGpWeayT-bgQUr2IoXHMNWGZdyVyxdUI",
  ];
  
  const athenaClientId = "0oak11xvyobnlP0qL297";
  
  if (specialClientIds.includes(clientId)) {
    return "openid patient/Observation.read patient/Patient.read patient/Procedure.read patient/AllergyIntolerance.read patient/CareTeam.read patient/Condition.read patient/Immunization.read patient/MedicationRequest.read patient/Medication.read";
  } else if (clientId === athenaClientId) {
    return 'openid fhirUser patient/Observation.read patient/Patient.read patient/Procedure.read patient/AllergyIntolerance.read patient/CareTeam.read patient/Condition.read patient/Immunization.read patient/MedicationRequest.read launch/patient';
  } else {
    return 'openid fhirUser user/Appointment.read user/MedicationOrder.read user/Observation.read user/Patient.read user/Procedure.read user/AllergyIntolerance.read user/CareTeam.read user/Condition.read user/Immunization.read user/MedicationRequest.read user/QuestionnaireResponse.read';
  }
};

// Helper function to build OAuth URL
const buildOAuthUrl = (
  authUrl: string,
  clientId: string,
  state: string,
  serverAddress: string,
  healthSystemId: string
): string => {
  const redirectUri = `${window.location.protocol}//${window.location.host}/source`;
  const scope = getOAuthScopes(clientId);
  
  const specialClientIds = [
    "yP7GArxnmCK0FmIftjwD3kc94fxpeZ5fCDNAxm-7r0E",
    "oO5OtwN1ZKqeg_VIEDoiNXbkkOIBRH0WrlCXWjPpJ-0",
    "JcjgosvOOHtRdThNyEsKndvuvLlQXfyKw_4XYwYns2Y",
    "T1SRnrUeCc48fkBjOvFo9d0oOEQGitl9wzn1AD9p0CU",
    "HVKi3Jrt9lUZGpWeayT-bgQUr2IoXHMNWGZdyVyxdUI",
  ];
  
  const athenaClientId = "0oak11xvyobnlP0qL297";
  
  let url = `${authUrl}?scope=${encodeURIComponent(scope)}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&client_id=${clientId}&state=${encodeURIComponent(state)}&aud=${encodeURIComponent(serverAddress)}`;
  
  if (specialClientIds.includes(clientId)) {
    url = url.replace(/%20/g, "+");
    url = url + '&practice_code=' + healthSystemId;
    url = url + '&code_challenge=' + 'Nij1Eje_8eVsUptUq1kfOruXN6DDCDlaiX7diQraFPU' + '&code_challenge_method=S256';
  } else if (clientId === athenaClientId) {
    url = "https://api.platform.athenahealth.com/oauth2/v1/authorize";
    url += '?scope=' + encodeURIComponent(scope) + '&response_type=code&redirect_uri=' + encodeURIComponent(redirectUri) + '&client_id=' + clientId + '&state=' + encodeURIComponent(state) + '&aud=' + encodeURIComponent(serverAddress);
    url = url + '&code_challenge=' + 'qjrzSW9gMiUgpUvqgEPE4_-8swvyCtfOVvg55o5S_es' + '&code_challenge_method=S256';
    url = url + '&noce=' + encodeURIComponent(state);
  }
  
  return url;
};

// Main function to initiate FHIR OAuth flow
export const initiateFHIROAuth = async (endpoint: HealthSystemEndpoint): Promise<void> => {
  console.log('Initiating FHIR OAuth for health system:', endpoint.name);
  
  const state = generateOAuthState(endpoint.id);
  let metadataURL = endpoint.address;
  if (!metadataURL.endsWith("/")) { 
    metadataURL += '/'; 
  }
  metadataURL += "metadata";
  
  try {
    console.log('Fetching metadata from:', metadataURL);
    const response = await fetch(metadataURL, {
      method: 'GET',
      headers: {
        'Accept': 'application/fhir+json'
      }
    });
    const metadata: FHIRMetadata = await response.json();
    
    console.log("FHIR metadata response:", metadata);
    
    // Store endpoint and metadata data in localStorage before redirect
    localStorage.setItem(`endpoint_${endpoint.id}`, JSON.stringify(endpoint));
    localStorage.setItem(`metadata_${endpoint.id}`, JSON.stringify(metadata));
    
    const extensions = metadata.rest[0].security.extension;
    const oauthExtension = extensions.find(e => e.url === "http://fhir-registry.smarthealthit.org/StructureDefinition/oauth-uris");
    
    if (oauthExtension && oauthExtension.extension) {
      const authorizeExtension = oauthExtension.extension.find(e => e.url === 'authorize');
      
      if (authorizeExtension && authorizeExtension.valueUri) {
        const clientId = getClientIdForServer(metadata, endpoint.id);
        
        if (clientId) {
          setCookie('import-state', state, 1);
          const serverAddress = endpoint.address.replace('/edge.zpaper.com/r/fhir', '');
          
          const oauthUrl = buildOAuthUrl(
            authorizeExtension.valueUri,
            clientId,
            state,
            serverAddress,
            endpoint.id
          );
          
          console.log("Redirecting to OAuth URL:", oauthUrl);
          window.location.href = oauthUrl;
        } else {
          throw new Error('Client ID not found for this health system');
        }
      } else {
        throw new Error('Authorization endpoint not found in metadata');
      }
    } else {
      throw new Error('OAuth configuration not found in metadata');
    }
  } catch (error) {
    console.error('Error initiating FHIR OAuth:', error);
    throw error;
  }
};
